/**
 * Consumer Storefront Detail — iter 48 · Phase C
 * Route: /store/:slug
 * Reads /api/v1/storefronts/{slug} + /offers, funnels offer-select into the
 * existing REVEAL / activation flow (which lives on CouponDetail.js).
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Store, ArrowRight, Sparkles, Tag, Filter, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import PageBackground from "@/components/PageBackground";
import Seo from "@/components/Seo";

function fmt(minor, currency = "CAD") {
  return ((minor || 0) / 100).toLocaleString("en-CA", { style: "currency", currency });
}

/**
 * Loop P4-SEO · JSON-LD structured data for a storefront.
 * Emits a schema.org LocalBusiness/Organization node plus every active
 * offer as an Offer node. Google + Bing use this to build rich brand cards.
 */
function StorefrontJsonLd({ sf, offers, canonicalUrl }) {
  if (!sf) return null;
  const org = {
    "@context": "https://schema.org",
    "@type": sf.online_offline_type === "offline" ? "LocalBusiness" : "Organization",
    name: sf.name,
    url: canonicalUrl,
    logo: sf.logo_url || undefined,
    image: sf.banner_image || sf.logo_url || undefined,
    description: sf.short_description || sf.long_description || undefined,
    sameAs: Object.values(sf.social_links || {}).filter(Boolean),
    address: sf.province ? {
      "@type": "PostalAddress",
      addressCountry: sf.country || "CA",
      addressRegion: sf.province,
      addressLocality: sf.city || undefined,
    } : undefined,
    makesOffer: (offers || []).slice(0, 20).map((o) => ({
      "@type": "Offer",
      name: o.title || o.name,
      description: o.short_description,
      url: `${canonicalUrl}#offer-${o.id}`,
      priceCurrency: (o.currency || "CAD").toUpperCase(),
      availability: "https://schema.org/InStock",
    })),
  };
  // Escape <, >, & and line separators so merchant text can never close the <script> element (stored XSS).
  const json = JSON.stringify(org).replace(/[<>&\u2028\u2029]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`);
  return (
    <script
      type="application/ld+json"
      data-testid="storefront-jsonld"
      dangerouslySetInnerHTML={{ __html: json }}
    />
  );
}

export default function StorefrontDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [sf, setSf] = useState(null);
  const [offers, setOffers] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/v1/storefronts/${slug}`),
      api.get(`/v1/storefronts/${slug}/offers?limit=100`),
    ]).then(([a, b]) => { setSf(a.data); setOffers(b.data.items || []); })
      .catch((e) => {
        // Store without a v1 storefront: show the store page instead of "Brand not found".
        if (e?.response?.status === 404) nav(`/store-legacy/${slug}`, { replace: true });
      })
      .finally(() => setLoading(false));
  }, [slug, nav]);

  if (loading) return (
    <div className="relative"><PageBackground variant="mint-cyan" />
      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10 text-slate-500" data-testid="storefront-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading brand…
      </div></div>
  );
  if (!sf) return (
    <div className="max-w-4xl mx-auto px-6 py-16 text-center text-sm text-slate-500" data-testid="storefront-detail-404">
      Brand not found. <Link to="/coupons" className="text-cyan-700 font-semibold">Back to brands</Link>
    </div>
  );

  const categories = Array.from(new Set(offers.map((o) => o.category).filter(Boolean)));
  const featured = offers.filter((o) => sf.featured_offer_ids?.includes(o.id));
  const filtered = category ? offers.filter((o) => o.category === category) : offers;

  const canonicalUrl = `https://www.cyannect.com/store/${slug}`;
  const seoTitle = (
    (sf.seo_title || "").trim() ||
    `${sf.name} coupons & offers · Cyannect`
  ).slice(0, 70);
  const seoDescription = (
    (sf.seo_description || "").trim() ||
    sf.short_description ||
    `Discover verified ${sf.primary_category || "brand"} coupons and cashback offers from ${sf.name} on Cyannect — Canada's performance-based offer marketplace.`
  ).slice(0, 160);
  const seoImage = (sf.og_image_url || sf.banner_image || sf.logo_url) || undefined;

  return (
    <div className="relative" data-testid="storefront-detail">
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        path={`/store/${slug}`}
        type="website"
        keywords={[sf.name, sf.primary_category, "cyannect", "coupons", "cashback", sf.province].filter(Boolean).join(", ")}
      />
      <StorefrontJsonLd sf={sf} offers={offers} canonicalUrl={canonicalUrl} />
      <PageBackground variant="mint-cyan" />

      {/* Banner */}
      <div className="h-48 md:h-56 relative overflow-hidden"
           style={{ backgroundImage: sf.banner_image ? `url(${sf.banner_image})` : undefined,
                    backgroundSize: "cover", backgroundPosition: "center",
                    background: sf.banner_image ? undefined : "linear-gradient(135deg,#0e7490 0%,#67e8f9 100%)" }}
           data-testid="storefront-banner">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/70" />
        <div className="relative z-10 max-w-6xl mx-auto h-full flex items-end px-6 pb-6">
          <div className="flex items-end gap-4 w-full">
            {sf.logo_url ? (
              <img src={sf.logo_url} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white p-1.5 shadow-xl object-contain shrink-0"
                   data-testid="storefront-logo" />
            ) : (
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white shadow-xl flex items-center justify-center shrink-0">
                <Store className="w-8 h-8 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0 text-white">
              <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="storefront-name">{sf.name}</h1>
              <div className="text-sm text-cyan-100 mt-1 flex items-center gap-2 flex-wrap">
                {sf.primary_category && <span className="pf-chip bg-white/15 border-white/20 text-white">{sf.primary_category}</span>}
                {sf.display_label && (
                  <span className="pf-chip bg-emerald-500/90 border-emerald-300 text-white font-semibold" data-testid="storefront-best-label">
                    <Sparkles className="w-3 h-3" /> {sf.display_label}
                  </span>
                )}
                <span>{sf.active_offer_count} active offer{sf.active_offer_count === 1 ? "" : "s"}</span>
              </div>
            </div>
            {sf.website_url && (
              <a href={sf.website_url} target="_blank" rel="noreferrer"
                 className="hidden md:inline-flex px-4 py-2 rounded-full bg-white text-slate-900 text-sm font-semibold items-center gap-2 hover:bg-slate-100 transition"
                 data-testid="storefront-website">
                Visit site <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 relative z-10 space-y-8">
        {sf.long_description && (
          <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">{sf.long_description}</p>
        )}

        {sf.best_value_label && (
          <div className="pf-card p-4 bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-200/60 flex items-center gap-3"
               data-testid="storefront-best-value">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <div className="text-sm">
              <span className="font-semibold text-emerald-800">Best value: {sf.best_value_label}</span>
              <span className="text-slate-500 ml-2">on the most-savings offer inside this brand</span>
            </div>
          </div>
        )}

        {/* Featured strip */}
        {featured.length > 0 && (
          <section data-testid="storefront-featured">
            <h2 className="font-display text-xl text-slate-900 mb-3">Featured offers</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {featured.map((o) => <OfferCard key={o.id} offer={o} nav={nav} />)}
            </div>
          </section>
        )}

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex gap-2 flex-wrap items-center" data-testid="storefront-cat-filter">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <button onClick={() => setCategory(null)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${!category ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    data-testid="storefront-cat-all">All</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${category === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      data-testid={`storefront-cat-${c}`}>{c}</button>
            ))}
          </div>
        )}

        {/* All offers grid */}
        <section data-testid="storefront-all-offers">
          <h2 className="font-display text-xl text-slate-900 mb-3">
            {category ? category : "All offers"}{" "}
            <span className="text-sm text-slate-500 font-normal">({filtered.length})</span>
          </h2>
          {filtered.length === 0 ? (
            <div className="pf-card p-8 text-center text-sm text-slate-500">No offers in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((o) => <OfferCard key={o.id} offer={o} nav={nav} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function OfferCard({ offer, nav }) {
  const label = offer.discount_type === "percent" ? `${offer.discount_value}% OFF` :
                offer.discount_type === "fixed" ? `$${offer.discount_value} OFF` :
                `${offer.discount_value} ${offer.discount_type || ""}`.trim();
  return (
    <div onClick={() => nav(`/coupons?id=${offer.id}`)}
         className="pf-card p-4 cursor-pointer hover:shadow-lg transition group"
         data-testid={`storefront-offer-${offer.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-emerald-100 to-cyan-100 flex items-center justify-center shrink-0">
          <Tag className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-slate-900 truncate group-hover:text-cyan-700 transition">{offer.name}</div>
          <div className="mt-1 pf-chip pf-chip-cyan text-[10px]">{label}</div>
          {offer.min_purchase && (
            <div className="text-xs text-slate-500 mt-1">Min ${offer.min_purchase}</div>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-cyan-700 transition" />
      </div>
    </div>
  );
}
